import React, { useEffect, useState } from 'react';
import api from '../../api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import {
  Mail, Download, Trash2, Loader2, Search, Users, UserCheck, UserX,
  Sparkles, ExternalLink,
} from 'lucide-react';

/**
 * Admin · Newsletter — gestion des inscrits à la newsletter.
 *
 * MVP : collecte d'emails opt-in. Pas d'envoi automatisé pour l'instant.
 * Export CSV pour réutilisation dans Brevo/Mailchimp/SendGrid quand le
 * volume justifiera un service d'emailing dédié.
 */
export default function AdminNewsletter() {
  const [data, setData] = useState({ items: [], total: 0, active: 0, unsubscribed: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const reload = async () => {
    setLoading(true);
    try {
      const d = await api.adminListNewsletter();
      setData(d);
    } catch (e) {
      toast.error("Impossible de charger les inscrits : " + e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, []);

  const remove = async (email) => {
    if (!window.confirm(`Supprimer définitivement "${email}" de la liste ?\n\n(Conformité RGPD : droit à l'effacement)`)) return;
    try {
      await api.adminDeleteNewsletterSubscriber(email);
      toast.success("Inscrit supprimé");
      await reload();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const exportCsv = (onlyActive = true) => {
    const url = api.adminNewsletterCsvUrl(onlyActive);
    const token = localStorage.getItem('access_token') || localStorage.getItem('token');
    // Création d'un téléchargement avec le header d'auth via un fetch + blob
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        if (!r.ok) throw new Error('Export refusé (auth ?)');
        return r.blob();
      })
      .then((blob) => {
        const a = document.createElement('a');
        const blobUrl = URL.createObjectURL(blob);
        a.href = blobUrl;
        a.download = `newsletter_${new Date().toISOString().slice(0, 10)}_${onlyActive ? 'actifs' : 'tous'}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(blobUrl);
        toast.success("Export CSV téléchargé");
      })
      .catch((e) => toast.error("Échec de l'export : " + e.message));
  };

  const filtered = (data.items || []).filter((s) =>
    !search ||
    s.email?.toLowerCase().includes(search.toLowerCase()) ||
    s.source?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="admin-newsletter-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-black tracking-tight flex items-center gap-2">
          <Mail className="w-6 h-6 text-[hsl(var(--primary))]" />
          Newsletter
        </h1>
        <p className="text-sm text-foreground/70 mt-1">
          Liste opt-in pour informer les élèves des nouveautés (éditions hebdo, modules, corrections).
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3 sm:gap-4">
        <div className="mc-card mc-card-accent-official p-4">
          <div className="flex items-center gap-2 text-foreground/70 text-xs font-bold uppercase tracking-wide">
            <Users className="w-4 h-4" /> Total
          </div>
          <div className="text-2xl sm:text-3xl font-black mt-1" data-testid="newsletter-stat-total">{data.total}</div>
        </div>
        <div className="mc-card mc-card-accent-success p-4">
          <div className="flex items-center gap-2 text-foreground/70 text-xs font-bold uppercase tracking-wide">
            <UserCheck className="w-4 h-4" /> Actifs
          </div>
          <div className="text-2xl sm:text-3xl font-black mt-1 text-emerald-400" data-testid="newsletter-stat-active">{data.active}</div>
        </div>
        <div className="mc-card p-4">
          <div className="flex items-center gap-2 text-foreground/70 text-xs font-bold uppercase tracking-wide">
            <UserX className="w-4 h-4" /> Désinscrits
          </div>
          <div className="text-2xl sm:text-3xl font-black mt-1 text-foreground/60" data-testid="newsletter-stat-unsub">{data.unsubscribed}</div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-3 items-center justify-between">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-foreground/40" />
          <Input
            placeholder="Rechercher par email ou source…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
            data-testid="newsletter-search"
          />
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => exportCsv(true)} className="gap-1.5" data-testid="newsletter-export-active">
            <Download className="w-4 h-4" /> Export actifs
          </Button>
          <Button variant="ghost" onClick={() => exportCsv(false)} className="gap-1.5" data-testid="newsletter-export-all">
            <Download className="w-4 h-4" /> Export complet
          </Button>
        </div>
      </div>

      {/* Liste */}
      {loading ? (
        <div className="flex items-center justify-center py-12 text-foreground/60">
          <Loader2 className="w-6 h-6 animate-spin mr-2" /> Chargement…
        </div>
      ) : filtered.length === 0 ? (
        <div className="mc-card p-10 text-center">
          <Sparkles className="w-12 h-12 mx-auto text-[hsl(var(--primary))]/40 mb-3" />
          <h3 className="text-lg font-black mb-2">
            {data.total === 0 ? 'Aucun inscrit pour l\'instant' : 'Aucun résultat'}
          </h3>
          <p className="text-sm text-foreground/70">
            {data.total === 0
              ? 'Les premiers inscrits apparaîtront ici dès qu\'ils s\'inscrivent depuis la page d\'accueil.'
              : 'Essaie un autre mot-clé.'}
          </p>
        </div>
      ) : (
        <div className="mc-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-card/40 border-b border-border">
                <tr>
                  <th className="text-left px-4 py-3 font-bold text-foreground/70">Email</th>
                  <th className="text-left px-4 py-3 font-bold text-foreground/70 hidden sm:table-cell">Source</th>
                  <th className="text-left px-4 py-3 font-bold text-foreground/70">Statut</th>
                  <th className="text-left px-4 py-3 font-bold text-foreground/70 hidden md:table-cell">Inscrit le</th>
                  <th className="text-right px-4 py-3 font-bold text-foreground/70">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((s) => (
                  <tr key={s.email} className="border-b border-border/50 hover:bg-card/30 transition-colors" data-testid={`newsletter-row-${s.email}`}>
                    <td className="px-4 py-3 font-mono text-xs sm:text-sm break-all">{s.email}</td>
                    <td className="px-4 py-3 text-foreground/70 hidden sm:table-cell">
                      <Badge variant="outline" className="text-[10px]">{s.source || '—'}</Badge>
                    </td>
                    <td className="px-4 py-3">
                      {s.status === 'active' ? (
                        <Badge variant="outline" className="bg-emerald-500/15 text-emerald-400 border-emerald-500/30 text-[10px]">Actif</Badge>
                      ) : (
                        <Badge variant="outline" className="bg-foreground/10 text-foreground/60 text-[10px]">Désinscrit</Badge>
                      )}
                    </td>
                    <td className="px-4 py-3 text-foreground/60 text-xs hidden md:table-cell">
                      {s.subscribed_at ? new Date(s.subscribed_at).toLocaleDateString('fr-FR') : '—'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button size="sm" variant="ghost" onClick={() => remove(s.email)} className="gap-1.5 text-destructive hover:text-destructive hover:bg-destructive/10" data-testid={`newsletter-delete-${s.email}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Aide / RGPD */}
      <div className="mc-card mc-card-accent-official p-4 text-sm text-foreground/75">
        <div className="flex items-start gap-2">
          <Mail className="w-4 h-4 mt-0.5 text-[hsl(var(--primary))] flex-shrink-0" />
          <div>
            <p className="font-bold text-foreground mb-2">Pour envoyer la newsletter</p>
            <ol className="list-decimal pl-4 space-y-1 leading-relaxed">
              <li>Clique sur <strong>Export actifs</strong> pour télécharger le CSV des inscrits actifs.</li>
              <li>Importe le CSV dans ton outil d'emailing (Brevo, Mailchimp, SendGrid…).</li>
              <li>Compose ton email (idéalement en lien avec l'édition hebdo en cours).</li>
              <li>Envoie ! Pense à inclure un lien de désinscription pointant vers <span className="font-mono text-xs">/newsletter/desinscription</span>.</li>
            </ol>
            <p className="mt-3 text-xs text-foreground/60 italic">
              💡 Quand le volume dépassera 100 inscrits, on pourra brancher un service d'envoi
              automatique (Brevo gratuit jusqu'à 300 emails/jour). Dis-le moi quand tu veux passer à l'étape suivante.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
